import {PolicyRecord} from 'ApplicationEntry/models/PolicyRecord';
import * as P from '../models/PolicyRecord';

import {createAction} from 'Shared/utilities/actionCreator';
import {reverseEnum} from 'Shared/utilities/enumUtility';
import {Async} from 'Core/models/Async';

export enum PolicyRecordActionType {
  CHANGE_BUSINESS_AREA = 'policyRecord/CHANGE_BUSINESS_AREA',
  CHANGE_STATUS = 'policyRecord/CHANGE_STATUS',
  CHANGE_WORK_TYPE = 'policyRecord/CHANGE_WORK_TYPE',
  CHANGE_CO_CODE = 'policyRecord/CHANGE_CO_CODE',
  CHANGE_POLICY_NUMBER = 'policyRecord/CHANGE_POLICY_NUMBER',
  CHANGE_BANK_NUMBER = 'policyRecord/CHANGE_BANK_NUMBER',
  CHANGE_SOCIAL_SECURITY_NUMBER = 'policyRecord/CHANGE_SOCIAL_SECURITY_NUMBER',
  CHANGE_FIRST_NAME = 'policyRecord/CHANGE_FIRST_NAME',
  CHANGE_LAST_NAME = 'policyRecord/CHANGE_LAST_NAME',
  CHANGE_AGENT = 'policyRecord/CHANGE_AGENT',
  CHANGE_CASE_NUMBER = 'policyRecord/CHANGE_CASE_NUMBER',
  CHANGE_PRIORITY_INCREASE = 'policyRecord/CHANGE_PRIORITY_INCREASE',
  CHANGE_STATE_SIGNED = 'policyRecord/CHANGE_STATE_SIGNED',
  CHANGE_PAYOR_NUMBER = 'policyRecord/CHANGE_PAYOR_NUMBER',
  GET_POLICY_RECORD = 'policyRecord/GET_POLICY_RECORD',
  UPDATE_POLICY_RECORD = 'policyRecord/UPDATE_POLICY_RECORD',
  CHANGE_ASYNC_POLICY_NUMBER = 'policyRecord/CHANGE_ASYNC_POLICY_NUMBER',
}

export const isPolicyRecordAction = (action: any): action is PolicyRecordAction =>
  action.type in reverseEnum(PolicyRecordActionType);

export const changeBusinessArea = (businessArea: string) =>
  createAction(PolicyRecordActionType.CHANGE_BUSINESS_AREA, {businessArea});
export const changeStatus = (status: string) => createAction(PolicyRecordActionType.CHANGE_STATUS, {status});
export const changeWorkType = (workType: string) => createAction(PolicyRecordActionType.CHANGE_WORK_TYPE, {workType});
export const changeCoCode = (coCode: string) => createAction(PolicyRecordActionType.CHANGE_CO_CODE, {coCode});
export const changeAsyncPolicyNumber = (policyNumber: Async<string>) =>
  createAction(PolicyRecordActionType.CHANGE_ASYNC_POLICY_NUMBER, {policyNumber});
export const changePolicyNumber = (policyNumber: string) =>
  createAction(PolicyRecordActionType.CHANGE_POLICY_NUMBER, {policyNumber});
export const changeBankNumber = (bankNumber: string) =>
  createAction(PolicyRecordActionType.CHANGE_BANK_NUMBER, {bankNumber});
export const changeSocialSecurityNumber = (socialSecurityNumber: string) =>
  createAction(PolicyRecordActionType.CHANGE_SOCIAL_SECURITY_NUMBER, {socialSecurityNumber});
export const changeFirstName = (firstName: string) =>
  createAction(PolicyRecordActionType.CHANGE_FIRST_NAME, {firstName});
export const changeLastName = (lastName: string) => createAction(PolicyRecordActionType.CHANGE_LAST_NAME, {lastName});
export const changeAgent = (agent: string) => createAction(PolicyRecordActionType.CHANGE_AGENT, {agent});
export const changeCaseNumber = (caseNumber: string) =>
  createAction(PolicyRecordActionType.CHANGE_CASE_NUMBER, {caseNumber});
export const changePriorityIncrease = (priorityIncrease: number) =>
  createAction(PolicyRecordActionType.CHANGE_PRIORITY_INCREASE, {priorityIncrease});
export const changeStateSigned = (stateSigned: string) =>
  createAction(PolicyRecordActionType.CHANGE_STATE_SIGNED, {stateSigned});
export const changePayorNumber = (payorNumber: string) =>
  createAction(PolicyRecordActionType.CHANGE_PAYOR_NUMBER, {payorNumber});
export const getPolicyRecord = () => createAction(PolicyRecordActionType.GET_POLICY_RECORD);
export const updatePolicyRecordDetails = (policyRecordDetails: PolicyRecord) =>
  createAction(PolicyRecordActionType.UPDATE_POLICY_RECORD, {policyRecordDetails});

export type PolicyRecordAction =
  | ReturnType<typeof changeBusinessArea>
  | ReturnType<typeof changeStatus>
  | ReturnType<typeof changeWorkType>
  | ReturnType<typeof changeCoCode>
  | ReturnType<typeof changePolicyNumber>
  | ReturnType<typeof changeAsyncPolicyNumber>
  | ReturnType<typeof changeBankNumber>
  | ReturnType<typeof changeSocialSecurityNumber>
  | ReturnType<typeof changeFirstName>
  | ReturnType<typeof changeLastName>
  | ReturnType<typeof changeAgent>
  | ReturnType<typeof changeCaseNumber>
  | ReturnType<typeof changePriorityIncrease>
  | ReturnType<typeof changeStateSigned>
  | ReturnType<typeof changePayorNumber>
  | ReturnType<typeof updatePolicyRecordDetails>;

type PolicyRecordState = PolicyRecord;
const DEFAULT_STATE: PolicyRecordState = P.EmptyPolicyRecord;

export default (state: PolicyRecordState = DEFAULT_STATE, action: PolicyRecordAction) => {
  switch (action.type) {
    case PolicyRecordActionType.CHANGE_BUSINESS_AREA:
      return P.changeBusinessArea(action.payload.businessArea)(state);
    case PolicyRecordActionType.CHANGE_STATUS:
      return P.changeStatus(action.payload.status)(state);
    case PolicyRecordActionType.CHANGE_WORK_TYPE:
      return P.changeWorkType(action.payload.workType)(state);
    case PolicyRecordActionType.CHANGE_CO_CODE:
      return P.changeCoCode(action.payload.coCode)(state);
    case PolicyRecordActionType.CHANGE_POLICY_NUMBER:
      return P.changePolicyNumber(action.payload.policyNumber)(state);
    case PolicyRecordActionType.CHANGE_ASYNC_POLICY_NUMBER:
      return P.changeAsyncPolicyNumber(action.payload.policyNumber)(state);
    case PolicyRecordActionType.CHANGE_BANK_NUMBER:
      return P.changeBankNumber(action.payload.bankNumber)(state);
    case PolicyRecordActionType.CHANGE_SOCIAL_SECURITY_NUMBER:
      return P.changeSocialSecurityNumber(action.payload.socialSecurityNumber)(state);
    case PolicyRecordActionType.CHANGE_FIRST_NAME:
      return P.changeFirstName(action.payload.firstName)(state);
    case PolicyRecordActionType.CHANGE_LAST_NAME:
      return P.changeLastName(action.payload.lastName)(state);
    case PolicyRecordActionType.CHANGE_AGENT:
      return P.changeAgent(action.payload.agent)(state);
    case PolicyRecordActionType.CHANGE_CASE_NUMBER:
      return P.changeCaseNumber(action.payload.caseNumber)(state);
    case PolicyRecordActionType.CHANGE_PRIORITY_INCREASE:
      return P.changePriorityIncrease(action.payload.priorityIncrease)(state);
    case PolicyRecordActionType.CHANGE_STATE_SIGNED:
      return P.changeStateSigned(action.payload.stateSigned)(state);
    case PolicyRecordActionType.CHANGE_PAYOR_NUMBER:
      return P.changePayorNumber(action.payload.payorNumber)(state);
    default:
      return state;
  }
};
